import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Link, useParams } from 'react-router-dom';
import { Dropdown, Table } from 'react-bootstrap';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { GET_STAGE_DETAILS, GET_STAGE_SUMMARY, StageDetail } from '../../../graphql/client/queries/stageQueries';
import { Loading, Error } from '../../_common';
import { Routes } from '../../../utils/Routes';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import ForbiddenError from '../../_common/Error/ForbiddenError';
import JobNavigation from '../JobNavigation';
import './StageSummary.scss';

const barChartColors = {
    completed: '#4171ca',
    preparing: '#ffc608',
    running: '#00FF00',
    paused: '#ee7420',
    remaining: '#a9a9a9',
};

const structureBarChart = (fillColor: string) => function StageBar(props: unknown) {
    const { x = 0, y = 0, width = 0, height = 0, value } = props as {
        x?: number; y?: number; width?: number; height?: number; value?: [number, number];
    };
    const count = value ? value[1] - value[0] : 0;
    if (count <= 0) return <g />;
    return <g>
        <rect x={x} y={y} width={width} height={height * 0.75} stroke="none" fill={fillColor} />
        <text x={x + width / 2} y={y + height * 0.75 / 2} fill="#000" fontSize={12}
            textAnchor="middle" alignmentBaseline="central">{count}</text>
    </g>;
};

export const stageSheetPath = (stageSheetId: number, history: boolean, bellyDump: boolean) => {
    const route = bellyDump
        ? (history ? Routes.STAGE_SHEET_BELLY_DUMP_RO : Routes.STAGE_SHEET_BELLY_DUMP)
        : (history ? Routes.STAGE_SHEET_RO : Routes.STAGE_SHEET);
    return route.route.replace(':stageSheetId', String(stageSheetId));
};

const StageList = ({ jobId, completed, wellheads, onJobName }: {
    jobId: number; completed: boolean; wellheads: string[]; onJobName?: (name: string) => void;
}) => {
    const [wellhead, setWellhead] = useState('ALL');
    const [page, setPage] = useState(0);
    const { data, loading, error } = useQuery(GET_STAGE_DETAILS, {
        variables: { input: { jobId, isCompleted: completed, pageNum: page, wellHead: encodeURIComponent(wellhead) } },
        fetchPolicy: 'network-only',
    });
    const details = data?.getStageDetails;
    useEffect(() => {
        if (details?.wellName) onJobName?.(details.wellName);
    }, [details?.wellName, onJobName]);
    const stages = (details?.stageDetails ?? []).filter((stage): stage is StageDetail => stage != null);
    const groups = new Map<number | null, StageDetail[]>();
    stages.forEach(stage => groups.set(stage.stageGroupId, [...(groups.get(stage.stageGroupId) ?? []), stage]));
    const orderedGroups = [...groups.entries()].sort(([first], [second]) => (second ?? 0) - (first ?? 0));
    const heading = completed ? 'Completed Stages' : 'Active Stages';

    return (
        <section aria-label={heading} className={completed ? 'StageSummary-Finished-Stages' : 'StageSummary-Active-Stages'}>
            <div className="StageSummary-Stages-Header">
                <h3 className="StageSummary-Stages-Header-Text">{heading}</h3>
                <Dropdown>
                    <Dropdown.Toggle variant="outline-dark" id={`stage-wellhead-${completed ? 'completed' : 'active'}`}
                        aria-label={`${heading} wellhead`} className="StageSummary-Wellhead-Filter">
                        {wellhead === 'ALL' ? 'Select Wellhead' : wellhead}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {['ALL', ...wellheads].map(name => <Dropdown.Item key={name} onClick={() => {
                            setWellhead(name);
                            setPage(0);
                        }}>{name === 'ALL' ? 'All Wellheads' : name}</Dropdown.Item>)}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            {error ? <Error error={error} /> : loading ? <div className="StageSummary-Loading"><Loading /></div> : stages.length === 0 ? (
                <div className="StageSummary-NoResult-Container">No {heading} Found</div>
            ) : <div className="StageSummary-TableContainer">
                <Table striped bordered className="custom-stage-summary">
                    <thead><tr>
                        <th>Wellhead</th><th>Stage #</th><th>Overall Stage #</th>
                        {completed ? <><th>Stage Start</th><th>Stage End</th><th>Notes</th></> : <><th>Start Date</th><th>Status</th></>}
                        <th>Frac Type</th><th>{completed ? 'Stage History' : 'Stage Sheet'}</th>
                    </tr></thead>
                    <tbody>
                        {orderedGroups.flatMap(([, group]) => group.map((stage, index) => {
                            const sheetStages = group.filter(other => other.stageSheetId === stage.stageSheetId);
                            const firstSheetRow = sheetStages[0] === stage;
                            const history = completed || sheetStages.every(other => other.status === 'Completed');
                            return <tr key={stage.jobStageId ?? `${stage.stageGroupId}-${index}`}>
                                <td>{stage.wellhead}</td><td>{stage.stage}</td><td>{stage.overallStage}</td>
                                <td>{stage.startDate}</td>
                                {completed ? <><td>{stage.endDate}</td><td className="StageSummary-Notes" title={stage.comments ?? ''}>{stage.comments}</td></>
                                    : <td>{stage.status}</td>}
                                {index === 0 && <td rowSpan={group.length} className="StageSummary-Fractype-Container">{stage.fracType}</td>}
                                {firstSheetRow && <td rowSpan={sheetStages.length} className="StageSummary-Stages-Button-Container">
                                    {!!stage.stageSheetId && <Link className="StageSummary-StageSheet-Link"
                                        to={stageSheetPath(stage.stageSheetId, history, details?.isBellyDump === true)}>
                                        {history ? 'Stage History' : 'Stage Sheet'}
                                    </Link>}
                                </td>}
                            </tr>;
                        }))}
                    </tbody>
                </Table>
            </div>}
            <div className={completed ? 'StageSummary-FinishedStages-Footer' : 'StageSummary-Stages-Footer'}>
                {!error && details?.isPagingRequired && <nav aria-label={`${heading} pages`} className="StageSummary-Stages-Prev-Next-Container">
                    <button type="button" className="StageSummary-Stages-Prev" aria-label={`${heading} previous page`}
                        disabled={loading || page === 0} onClick={() => setPage(page - 1)}>
                        <i className="material-icons" aria-hidden="true">chevron_left</i>Previous
                    </button>
                    <button type="button" className="StageSummary-Stages-Next" aria-label={`${heading} next page`}
                        disabled={loading || !!details.isLastItem} onClick={() => setPage(page + 1)}>
                        Next<i className="material-icons" aria-hidden="true">chevron_right</i>
                    </button>
                </nav>}
            </div>
        </section>
    );
};

const StageSummaryContent = ({ jobId, jobName }: { jobId: number; jobName?: string | null }) => {
    const { data, loading, error } = useQuery(GET_STAGE_SUMMARY, { variables: { jobId }, fetchPolicy: 'network-only' });
    const [resolvedJobName, setResolvedJobName] = useState('');
    const summary = (data?.getStageSummary ?? []).filter(item => item != null);
    const wellheads = [...new Set(summary.map(item => item.wellhead))];

    return <div className="StageSummary-Container">
        <header className="StageSummary-Header-Container">
            <h2 className="StageSummary-Header">Stage Summary: {jobName || resolvedJobName}</h2>
            <JobNavigation jobId={jobId} active="stages" variant="stage" />
        </header>
        <div className="StageSummary-Graph-Container" role="img" aria-label="Stage counts by wellhead">
            {error ? <Error error={error} /> : loading ? <div className="StageSummary-Loading"><Loading /></div> : summary.length === 0 ? (
                <div className="StageSummary-NoResult-Container">No Stage Summary Found</div>
            ) : <ResponsiveContainer width="95%" height={summary.length < 3 ? 200 : 400}>
                <BarChart data={summary} layout="vertical" margin={{ top: 20, bottom: 15 }}>
                    <XAxis type="number" tick={false} />
                    <YAxis type="category" dataKey="wellhead" width={135} style={{ textAnchor: 'end', fontSize: '12px', fill: 'black' }} />
                    {Object.entries(barChartColors).map(([status, color]) => <Bar key={status} dataKey={status}
                        stackId="a" fill={color} shape={structureBarChart(color)} />)}
                    <Legend formatter={value => <span className="StageSummary-Graph-Legend">{value}</span>} />
                    <Tooltip shared={false} content={({ active, payload }) => {
                        const item = payload?.[0];
                        return active && item ? <div className="StageSummary-Graph-Tooltip-Container" style={{ backgroundColor: item.fill }}>
                            <p className="StageSummary-Graph-Tooltip-Data">wellhead : {item.payload.wellhead}</p>
                            <p className="StageSummary-Graph-Tooltip-Data">{String(item.dataKey)} : {item.value}</p>
                        </div> : null;
                    }} />
                </BarChart>
            </ResponsiveContainer>}
        </div>
        <StageList key={`${jobId}-active`} jobId={jobId} completed={false} wellheads={wellheads} onJobName={setResolvedJobName} />
        <StageList key={`${jobId}-finished`} jobId={jobId} completed wellheads={wellheads} />
    </div>;
};

export const StageReadAccess = ({ children }: { children: React.ReactNode }) => {
    const auth = useAuthContext();
    return auth.userHasPermission(PermissionEnum.Carrier) ? <ForbiddenError /> : <>{children}</>;
};

const JobStageSummary = ({ jobId: suppliedJobId, jobName }: { jobId?: number; jobName?: string | null }) => {
    const params = useParams();
    const jobId = suppliedJobId ?? Number(params.jobId);
    return <StageReadAccess>{Number.isSafeInteger(jobId) && jobId > 0
        ? <StageSummaryContent jobId={jobId} jobName={jobName} />
        : <p role="alert">Invalid job.</p>}</StageReadAccess>;
};

export default JobStageSummary;